<template>
  <div>
    <v-dialog
      v-model="createEditModal"
      @click:outside="closeDialog"
    >
      <v-card>
        <v-card-title class="justify-space-between third white--text">
          Chute
          <v-btn icon @click="closeDialog">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="card-body">
          <v-autocomplete
            v-model="fall.session"
            chips
            clearable
            deletable-chips
            small-chips
            hide-details
            label="Session"
            color="third"
            :items="sessions"
            item-value="id"
            item-text="label"
            class="mb-4"
            return-object
            :disabled="isEdit"
          >
            <template #selection="data">
              <v-chip
                v-bind="data.attrs"
                :input-value="data.selected"
                close
                :disabled="isEdit"
                @click="data.select"
                @click:close="clearFallFields(data.item)"
              >
                {{ data.item.label }}
              </v-chip>
            </template>
          </v-autocomplete>
          <v-text-field
            :value="sessionInstructor ? sessionInstructor.fullname : null"
            label="Moniteur"
            color="third"
            hide-details
            readonly
            class="mb-4"
          />
          <v-autocomplete
            v-model="fall.ridder"
            chips
            clearable
            deletable-chips
            small-chips
            hide-details
            label="Participant"
            color="third"
            :items="participants"
            item-value="uuid"
            item-text="fullname"
            class="mb-6"
            return-object
            :disabled="isEdit || !!selectParticipant"
          />
          <v-text-field
            v-model="participantHorseName"
            label="Cheval"
            color="third"
            hide-details
            readonly
            class="mb-6"
            prepend-icon="mdi-horse"
          />
          <v-menu
            ref="menu"
            v-model="datePicker"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template #activator="{ on, attrs }">
              <v-form v-model="hoursValid">
                <v-text-field
                  v-model="fall.time"
                  label="Heure"
                  prepend-icon="mdi-clock-time-four-outline"
                  :rules="[rules.required, rules.hourBetween]"
                  v-bind="attrs"
                  color="third"
                  :disabled="!fall.session"
                  v-on="on"
                />
              </v-form>
            </template>
            <v-time-picker
              v-if="datePicker"
              v-model="fall.time"
              full-width
              color="third"
              format="24hr"
              :max="maxTime"
              :min="minTime"
            >
              <v-spacer />
              <v-btn
                text
                color="third"
                @click="datePicker = false"
              >
                Annuler
              </v-btn>
              <v-btn
                text
                color="third"
                :disabled="!hoursValid"
                @click="$refs.menu.save(fall.time)"
              >
                Confirmer
              </v-btn>
            </v-time-picker>
          </v-menu>
          <v-switch
            v-model="fall.firefighter"
            label="Intervention Pompiers?"
            color="third"
            hide-details
            class="mb-4"
          />
          <v-textarea
            v-model="fall.description"
            label="Description de la chute"
            no-resize
            color="third"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="justify-end">
          <v-btn class="third--text" text @click="closeDialog">
            Annuler
          </v-btn>
          <v-btn class="third--text" text :disabled="isAddEditDisabled || loading || !hoursValid" @click="createEditFall">
            {{ btnLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ref, computed, inject, onBeforeMount, watch } from '@vue/composition-api';
import { format, fromUnixTime, getUnixTime, set, isAfter, isBefore, addSeconds, subSeconds } from 'date-fns';
import { fr } from 'date-fns/esm/locale';
import store from '@/store';

import { createFall, editFall } from '@/helpers/request';
import { sendNotification } from '@/helpers/utils';

export default {
  props: {
    createFall: {
      type: Boolean,
      default: false
    }
  },
  setup (props, context) {
    const datePicker = ref(null);
    const hoursValid = ref(false);

    const createEditModal = inject('createEditModal', ref(false));
    const isEdit = inject('isEdit', ref(false));
    const loading = inject('fallLoading', ref(false));
    const selectParticipant = inject('selectParticipant', ref(null));

    const fall = ref({
      uuid: null,
      session: null,
      ridder: null,
      horse: null,
      description: null,
      firefighter: false,
      time: null,
      instructor: null
    });

    const checkHours = hourMinutes => {
      if (!hourMinutes) {
        return false;
      }

      const hour = hourMinutes.split(':')[0];
      const minutes = hourMinutes.split(':')[1];

      const selectedDate = set(new Date(fall.value.session.start_time * 1000), { hours: hour, minutes: minutes });

      return isAfter(selectedDate, subSeconds(new Date(fall.value.session.start_time * 1000), 1)) && isBefore(selectedDate, addSeconds(new Date(fall.value.session.end_time * 1000), 1));
    };

    const rules = ref({
      required: value => !!value || 'Champs requis.',
      hourBetween: value => checkHours(value) || 'L\'heure sélectionnée ne fait pas partie de la session sélectionnée.'
    });

    const originalFall = inject('originalFall', ref({ ...fall }));

    const selectedFall = inject('selectedFall', ref(fall));

    const sessions = computed(() => {
      const storeSessions = store.state.sessions.map(s => ({
        ...s,
        label: `${s.name} - ${format(fromUnixTime(s.start_time), 'PPPP - HH:mm', { locale: fr })} `
      }));

      const groupedSessions = {};
      storeSessions.forEach(session => {
        const startTime = session.start_time;
        if (!groupedSessions[startTime]) {
          groupedSessions[startTime] = [];
        }
        groupedSessions[startTime].push(session);
      });

      const sortedSessionsArray = [];

      Object.values(groupedSessions).forEach(sessionsGroup => {
        sessionsGroup.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        sessionsGroup.forEach(s => sortedSessionsArray.push(s));
      });

      if (selectParticipant.value) {
        return sortedSessionsArray.filter(s => (s.participants || []).some(p => p.uuid === selectParticipant.value.uuid));
      }

      return sortedSessionsArray;
    });

    const participants = computed(() => {
      let participantsToRetrun = [];

      if (selectParticipant.value) {
        participantsToRetrun.push(selectParticipant.value);
      }

      const session = sessions.value.find(s => s.id === fall.value.session?.id);

      if (session) {
        participantsToRetrun = [...(session.participants || []).map(p => ({ ...p, fullname: `${p.firstname} ${p.lastname}` })), ...participantsToRetrun];
      }

      return participantsToRetrun;
    });

    const participantHorse = computed(() => {
      if (fall.value.ridder) {
        const session = sessions.value.find(s => s.id === fall.value.session?.id);
        const sessionParticipant = session ? session.participants.find(p => p.uuid === fall.value.ridder.uuid) : null;

        if (sessionParticipant && sessionParticipant.horse) {
          const horse = store.getters.horse(sessionParticipant.horse);

          return horse || null;
        }
      }

      return null;
    });

    const isAddEditDisabled = computed(() => !fall.value.session || !fall.value.ridder || !participantHorse.value || !fall.value.description || !fall.value.time);

    const participantHorseName = computed(() => (participantHorse.value ? participantHorse.value.horse_name : null));

    const sessionInstructor = computed(() => {
      if (!fall.value || !fall.value.session || !fall.value.session.instructors || !fall.value.session.instructors.length) {
        return null;
      }

      const instructor = store.getters.instructor(fall.value.session.instructors[0].uuid);

      if (!instructor) {
        return null;
      }
      return { ...instructor, fullname: `${instructor.firstname} ${instructor.lastname}` };
    });

    const maxTime = computed(() => format(fromUnixTime(fall.value.session.end_time), 'HH:mm'));

    const minTime = computed(() => format(fromUnixTime(fall.value.session.start_time), 'HH:mm'));

    const closeDialog = () => {
      createEditModal.value = !createEditModal.value;
    };

    const selectedReceivedParticipant = () => {
      if (selectParticipant.value) {
        fall.value.ridder = selectParticipant.value;
      }
    };

    const clearFallFields = () => {
      fall.value = { ...originalFall.value };
      selectedReceivedParticipant();
    };

    const computeFallBody = includeSession => {
      const fallBody = {
        date: getUnixTime(new Date(`${format(fromUnixTime(fall.value.session.start_time), 'yyyy-MM-dd')}T${fall.value.time}`)),
        ridder: {
          uuid: fall.value.ridder.uuid,
          name: `${fall.value.ridder.firstname} ${fall.value.ridder.lastname}`
        },
        horse: participantHorse.value ? participantHorse.value.uuid : null,
        description: fall.value.description,
        firefighter: fall.value.firefighter,
        instructor: sessionInstructor.value ? { uuid: sessionInstructor.value.uuid, name: sessionInstructor.value.fullname } : null,
        workspace: store.state.workspace.code
      };

      if (includeSession) {
        fallBody.session = {
          id: fall.value.session.id,
          name: fall.value.session.name
        };
      }

      return fallBody;
    };

    const createSessionFall = async () => {
      loading.value = true;

      const fallToCreate = computeFallBody(true);

      const response = await createFall(fallToCreate);

      if (response.success) {
        context.emit('addEditFall', { ...response.data, create: true });
      }

      loading.value = false;
      clearFallFields();
      sendNotification(response.success ? 'success' : 'error', response.message);
      closeDialog();
    };

    const editSessionFall = async () => {
      loading.value = true;

      const body = computeFallBody(false);

      const fallToEdit = { uuid: fall.value.uuid, ...body };

      const response = await editFall(fallToEdit);

      if (response.success) {
        context.emit('addEditFall', fallToEdit);
      }

      loading.value = false;
      clearFallFields();
      sendNotification(response.success ? 'success' : 'error', response.message);
      closeDialog();
    };

    const createEditFall = async () => {
      if (isEdit.value && !props.createFall) {
        editSessionFall();
        return;
      }
      await createSessionFall();
    };

    watch(() => fall.value.session, (newSession, oldSession) => {
      if (newSession && newSession.start_time) {
        const time = fall.value.time || format(newSession.start_time * 1000, 'HH:mm');
        fall.value.time = time;
      }
    });

    const btnLabel = computed(() => (isEdit.value && !props.createFall ? 'Modifier le chute' : 'Enregistrer'));

    onBeforeMount(() => {
      fall.value = selectedFall.value;
      selectedReceivedParticipant();
    });

    return {
      hoursValid,
      checkHours,
      rules,
      sessionInstructor,
      selectParticipant,
      isAddEditDisabled,
      isEdit,
      participantHorseName,
      maxTime,
      minTime,
      datePicker,
      clearFallFields,
      createEditFall,
      participantHorse,
      participants,
      btnLabel,
      createEditModal,
      closeDialog,
      sessions,
      fall,
      loading
    };
  }
};
</script>
